<script setup lang="ts">
/**
 * Bühne: misst den Container (pxPerMm), zentriert die Fläche, zeichnet Guides/Handles und
 * verarbeitet Gesten (Auswahl, Verschieben, Griffe, Rotation, In-Slot-Ausschnitt, Pinch).
 * Alle Layer-Positionen bleiben in mm; Pixel existieren nur hier.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { DesignDocument, Frame, ImageLayer, Layer, ProductGeometry } from '@pfe/design-renderer'
import { useDesignStore } from '../../store/useDesignStore'
import DesignSurface from './DesignSurface.vue'
import LayerHandles from './LayerHandles.vue'
import GuidesOverlay from '../editor/GuidesOverlay.vue'
import { Maximize2, Minimize2, Minus, Plus } from 'lucide-vue-next'
import { PointerSession } from '../../design/gestures/pointerSession'
import { twoPointerDelta, twoPointerState, type TwoPointerState } from '../../design/gestures/twoPointer'
import { resizeFrame, rotationFromPointer, unrotatePoint, type HandleName } from '../../design/gestures/transformMath'
import { snapFrame, type SnapLine } from '../../design/gestures/snapping'
import { isCoarsePointer } from '../../config/breakpoints'
import { measureFittedSizePt, measureOverflowingTextLayers } from '@pfe/design-renderer/dom'
import type { UploadRef } from '@pfe/design-renderer'
import { DESIGN_UPLOAD_MIME, getDraggedUpload } from '../../design/dragTypes'

const props = withDefaults(defineProps<{
  doc: DesignDocument
  geometry: ProductGeometry | null
  guides: 'full' | 'cut-only' | 'off'
  productView: boolean
  snapEnabled?: boolean
}>(), { snapEnabled: true })

export type StageDropTarget = { layerId: string | null; xMm: number; yMm: number }
const emit = defineEmits<{
  (e: 'drop-upload', upload: UploadRef, target: StageDropTarget): void
  (e: 'drop-files', files: File[], target: StageDropTarget): void
}>()

const store = useDesignStore()
const wrapEl = ref<HTMLElement | null>(null)
const containerW = ref(0)
const containerH = ref(0)
const zoom = ref(1)
const coarse = isCoarsePointer()

const fullW = computed(() => props.doc.canvas.widthMm + 2 * props.doc.canvas.bleedMm)
const fullH = computed(() => props.doc.canvas.heightMm + 2 * props.doc.canvas.bleedMm)
const pxPerMm = computed(() => {
  const pad = coarse ? 24 : 48
  const availW = Math.max(100, containerW.value - pad * 2)
  const availH = Math.max(100, containerH.value - pad * 2)
  const fit = Math.min(availW / Math.max(1, fullW.value), availH / Math.max(1, fullH.value))
  return Math.max(0.2, fit * zoom.value)
})
const surfaceStyle = computed(() => ({ width: `${fullW.value * pxPerMm.value}px`, height: `${fullH.value * pxPerMm.value}px` }))
const selectedLayer = computed(() => store.selectedLayer)
const snapLines = ref<SnapLine[]>([])

let ro: ResizeObserver | null = null
onMounted(() => {
  ro = new ResizeObserver((entries) => {
    const r = entries[0]?.contentRect
    if (r) { containerW.value = r.width; containerH.value = r.height }
  })
  if (wrapEl.value) ro.observe(wrapEl.value)
  window.addEventListener('keydown', onKey)
})
onBeforeUnmount(() => { ro?.disconnect(); window.removeEventListener('keydown', onKey) })

// ---------- Koordinaten ----------
const surfaceEl = ref<HTMLElement | null>(null)
function toMm(clientX: number, clientY: number): { x: number; y: number } {
  const el = surfaceEl.value
  if (!el) return { x: 0, y: 0 }
  const r = el.getBoundingClientRect()
  return { x: (clientX - r.left) / pxPerMm.value - props.doc.canvas.bleedMm, y: (clientY - r.top) / pxPerMm.value - props.doc.canvas.bleedMm }
}
function layerUnderPointerExcluding(clientX: number, clientY: number, excludeId: string): Layer | null {
  const els = document.elementsFromPoint(clientX, clientY)
  for (const el of els) {
    const node = (el as HTMLElement).closest?.('.dr-layer[data-layer-id]') as HTMLElement | null
    const id = node?.dataset.layerId
    if (!id || id === excludeId) continue
    return props.doc.layers.find((l) => l.id === id) ?? null
  }
  return null
}

function layerAt(clientX: number, clientY: number): Layer | null {
  const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null
  const node = el?.closest?.('.dr-layer[data-layer-id]') as HTMLElement | null
  if (!node) return null
  const id = node.dataset.layerId || ''
  const layer = props.doc.layers.find((l) => l.id === id) ?? null
  if (!layer || layer.constraints.locked && layer.constraints.contentOnly === false && layer.constraints.role === 'address-lines') return null
  return layer
}

// ---------- Gesten ----------
type Mode = 'none' | 'move' | 'resize' | 'rotate' | 'slot' | 'pinch' | 'stage-pan'
const session = new PointerSession(3)
let mode: Mode = 'none'
let activeId: string | null = null
let startFrame: Frame | null = null
let startFit: ImageLayer['fit'] | null = null
let startMm = { x: 0, y: 0 }
let handle: HandleName | null = null
let pinchStart: TwoPointerState | null = null
let lastTapAt = 0
let lastTapId: string | null = null

function onPointerDown(e: PointerEvent) {
  if (e.button !== undefined && e.button !== 0 && e.pointerType === 'mouse') return
  const target = e.target as HTMLElement | null
  // Bedienelemente auf der Bühne und der gerade bearbeitete Text bekommen ihre Klicks selbst
  // (sonst schluckt setPointerCapture den click bzw. die Cursor-Platzierung im Text)
  if (target?.closest('.stage__zoom')) return
  if (store.editingTextId && target?.closest(`[data-layer-id="${store.editingTextId}"]`)) return
  session.down(e)
  ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
  if (session.count === 2 && selectedLayer.value) {
    const [a, b] = [...session.pointers.values()]
    pinchStart = twoPointerState(a!, b!)
    startFrame = { ...selectedLayer.value.frame }
    startFit = selectedLayer.value.type === 'image' ? { ...(selectedLayer.value as ImageLayer).fit } : null
    mode = 'pinch'
    return
  }
  if (session.count > 1) return
  if (store.editingTextId) {
    const hit = layerAt(e.clientX, e.clientY)
    if (hit?.id === store.editingTextId) return // Text bearbeiten läuft
    store.editingTextId = null
  }
  const hit = layerAt(e.clientX, e.clientY)
  if (!hit) {
    store.select([])
    store.inSlotEditId = null
    mode = 'none'
    return
  }
  const now = e.timeStamp
  const isDouble = lastTapId === hit.id && now - lastTapAt < 350
  lastTapAt = now
  lastTapId = hit.id
  if (isDouble) {
    if (hit.type === 'text' && !hit.constraints.locked) { store.select([hit.id]); store.editingTextId = hit.id; mode = 'none'; return }
    if (hit.type === 'image' && (hit as ImageLayer).source.kind !== 'placeholder') { store.select([hit.id]); store.inSlotEditId = hit.id }
  }
  if (!store.selection.includes(hit.id)) store.select([hit.id])
  startMm = toMm(e.clientX, e.clientY)
  startFrame = { ...hit.frame }
  activeId = hit.id
  if (store.inSlotEditId === hit.id && hit.type === 'image') {
    startFit = { ...(hit as ImageLayer).fit }
    mode = 'slot'
  } else if (hit.constraints.locked || (hit.constraints.contentOnly && !store.templateMode)) {
    mode = 'none'
  } else {
    mode = 'move'
  }
  store.beginTxn()
}

function onHandleDown(h: HandleName, e: PointerEvent) {
  const layer = selectedLayer.value
  if (!layer) return
  session.down(e)
  ;(wrapEl.value as HTMLElement).setPointerCapture?.(e.pointerId)
  mode = 'resize'
  handle = h
  activeId = layer.id
  startFrame = { ...layer.frame }
  startMm = toMm(e.clientX, e.clientY)
  store.beginTxn()
}
function onRotateDown(e: PointerEvent) {
  const layer = selectedLayer.value
  if (!layer) return
  session.down(e)
  ;(wrapEl.value as HTMLElement).setPointerCapture?.(e.pointerId)
  mode = 'rotate'
  activeId = layer.id
  startFrame = { ...layer.frame }
  store.beginTxn()
}

function onPointerMove(e: PointerEvent) {
  session.move(e)
  if (mode === 'none' || !activeId || !startFrame) return
  const layer = props.doc.layers.find((l) => l.id === activeId)
  if (!layer) return
  const p = toMm(e.clientX, e.clientY)

  if (mode === 'pinch' && pinchStart && session.count === 2) {
    const [a, b] = [...session.pointers.values()]
    const d = twoPointerDelta(pinchStart, twoPointerState(a!, b!))
    if (layer.type === 'image' && store.inSlotEditId === layer.id && startFit) {
      store.patchLayer(layer.id, { fit: { ...startFit, scale: Math.max(1, Math.min(8, startFit.scale * d.scale)) } } as any)
    } else if (!layer.constraints.locked && (!layer.constraints.contentOnly || store.templateMode)) {
      const w = Math.max(3, startFrame.wMm * d.scale)
      const h = Math.max(3, startFrame.hMm * d.scale)
      const cx = startFrame.xMm + startFrame.wMm / 2 + d.dx / pxPerMm.value
      const cy = startFrame.yMm + startFrame.hMm / 2 + d.dy / pxPerMm.value
      store.patchLayer(layer.id, { frame: { ...startFrame, xMm: cx - w / 2, yMm: cy - h / 2, wMm: w, hMm: h, rotationDeg: (startFrame.rotationDeg + d.rotationDeg + 360) % 360 } })
    }
    return
  }

  if (mode === 'move') {
    const raw = { ...startFrame, xMm: startFrame.xMm + (p.x - startMm.x), yMm: startFrame.yMm + (p.y - startMm.y) }
    const others = props.doc.layers.filter((l) => l.id !== layer.id)
    const snapped = snapFrame(raw, props.doc.canvas, others, 6 / pxPerMm.value, props.snapEnabled && !e.altKey)
    snapLines.value = snapped.lines
    store.patchLayer(layer.id, { frame: snapped.frame })
    return
  }
  if (mode === 'resize' && handle) {
    const a = unrotatePoint(p.x, p.y, startFrame)
    const s = unrotatePoint(startMm.x, startMm.y, startFrame)
    const keepAspect = handle.length === 2 ? !e.shiftKey : false
    store.patchLayer(layer.id, { frame: resizeFrame(startFrame, handle, a.x - s.x, a.y - s.y, layer.type === 'image' ? keepAspect : keepAspect && layer.type !== 'text') })
    return
  }
  if (mode === 'rotate') {
    store.patchLayer(layer.id, { frame: { ...startFrame, rotationDeg: rotationFromPointer(startFrame, p.x, p.y, !e.shiftKey) } })
    return
  }
  if (mode === 'slot' && layer.type === 'image' && startFit) {
    const dxMm = p.x - startMm.x
    const dyMm = p.y - startMm.y
    const src = layer.source.kind !== 'placeholder' ? layer.source : null
    if (!src) return
    // Verschiebung in Offset-Anteile umrechnen (Spielraum = Bildgröße − Rahmen)
    const fw = layer.frame.wMm
    const fh = layer.frame.hMm
    const base = Math.max(fw / (src.width || 1), fh / (src.height || 1)) * startFit.scale
    const rangeX = (src.width || 1) * base - fw
    const rangeY = (src.height || 1) * base - fh
    const ox = rangeX > 0 ? Math.max(0, Math.min(1, startFit.offsetX - dxMm / rangeX)) : 0.5
    const oy = rangeY > 0 ? Math.max(0, Math.min(1, startFit.offsetY - dyMm / rangeY)) : 0.5
    store.patchLayer(layer.id, { fit: { ...startFit, offsetX: ox, offsetY: oy } } as any)
  }
}

function onPointerUp(e: PointerEvent) {
  session.up(e)
  ;(wrapEl.value as HTMLElement)?.releasePointerCapture?.(e.pointerId)
  if (mode === 'pinch') {
    if (session.count === 0) { store.commitTxn(); mode = 'none'; pinchStart = null }
    return
  }
  if (mode === 'move' && activeId) {
    // Bild über einem anderen Bild-Slot losgelassen -> Quellen tauschen statt verschieben
    const moving = props.doc.layers.find((l) => l.id === activeId)
    if (moving?.type === 'image' && moving.source.kind !== 'placeholder' && session.moved) {
      const target = layerUnderPointerExcluding(e.clientX, e.clientY, moving.id)
      if (target?.type === 'image' && !target.constraints.locked) {
        store.cancelTxn()
        store.swapSlots(moving.id, target.id)
        mode = 'none'; handle = null; activeId = null; startFrame = null; startFit = null; snapLines.value = []
        return
      }
    }
  }
  if (mode !== 'none') store.commitTxn()
  mode = 'none'
  handle = null
  activeId = null
  startFrame = null
  startFit = null
  snapLines.value = []
}

function onWheel(e: WheelEvent) {
  if (!(e.ctrlKey || e.metaKey)) return
  e.preventDefault()
  const layer = selectedLayer.value
  if (layer && store.inSlotEditId === layer.id && layer.type === 'image') {
    const fit = (layer as ImageLayer).fit
    store.beginTxn()
    store.patchLayer(layer.id, { fit: { ...fit, scale: Math.max(1, Math.min(8, fit.scale * Math.exp(-e.deltaY * 0.002))) } } as any)
    store.commitTxn()
    return
  }
  zoom.value = Math.max(0.5, Math.min(4, zoom.value * Math.exp(-e.deltaY * 0.002)))
}

function onKey(e: KeyboardEvent) {
  if (store.editingTextId) return
  const target = e.target as HTMLElement | null
  if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
  const meta = e.metaKey || e.ctrlKey
  if (meta && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? store.redo() : store.undo(); return }
  if (meta && e.key.toLowerCase() === 'y') { e.preventDefault(); store.redo(); return }
  const layer = selectedLayer.value
  if (!layer) return
  if (e.key === 'Escape') { store.select([]); store.inSlotEditId = null; return }
  if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); store.removeLayers([layer.id]); return }
  if (meta && e.key.toLowerCase() === 'd') { e.preventDefault(); store.duplicateLayer(layer.id); return }
  if (meta && e.key === ']') { e.preventDefault(); store.reorder(layer.id, e.shiftKey ? 'top' : 'up'); return }
  if (meta && e.key === '[') { e.preventDefault(); store.reorder(layer.id, e.shiftKey ? 'bottom' : 'down'); return }
  if (e.key === 'Enter' && layer.type === 'text') { e.preventDefault(); store.editingTextId = layer.id; return }
  const step = e.shiftKey ? 10 : 1
  const d: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }
  if (d[e.key] && !layer.constraints.locked && (!layer.constraints.contentOnly || store.templateMode)) {
    e.preventDefault()
    store.beginTxn()
    store.patchLayer(layer.id, { frame: { ...layer.frame, xMm: layer.frame.xMm + d[e.key]![0], yMm: layer.frame.yMm + d[e.key]![1] } })
    store.commitTxn()
  }
}

function onTextInput(id: string, text: string) {
  store.patchLayer(id, { text } as any)
}
function onTextDone() {
  store.editingTextId = null
}

// ---------- Vollbild (ganzer Editor, damit Werkzeuge erreichbar bleiben) ----------
const isFullscreen = ref(false)
function syncFullscreen() { isFullscreen.value = !!document.fullscreenElement }
async function toggleFullscreen() {
  try {
    if (document.fullscreenElement) await document.exitFullscreen()
    else {
      const root = (wrapEl.value?.closest('.dp') as HTMLElement | null) ?? document.documentElement
      await root.requestFullscreen()
    }
  } catch { /* z. B. iOS Safari ohne Fullscreen-API */ }
}
onMounted(() => document.addEventListener('fullscreenchange', syncFullscreen))
onBeforeUnmount(() => document.removeEventListener('fullscreenchange', syncFullscreen))
watch(() => store.editingTextId, (id, prev) => {
  if (prev && !id) {
    // Textänderungen als ein Undo-Schritt festhalten
    store.commitTxn()
  } else if (id && !prev) {
    store.beginTxn()
  }
})

const guideAspect = computed(() => fullW.value / Math.max(1, fullH.value))

// ---------- Text: Autofit (shrink) mit dem echten DOM messen ----------
let measureTimer: ReturnType<typeof setTimeout> | null = null
const textSignature = computed(() => props.doc.layers
  .filter((l) => l.type === 'text')
  .map((l) => l.type === 'text' ? `${l.id}|${l.text}|${l.font.familyId}|${l.font.sizePt}|${l.font.weight}|${l.font.italic}|${l.font.lineHeight}|${l.font.letterSpacingEm}|${l.frame.wMm}x${l.frame.hMm}|${l.autoFit}` : '')
  .join('\n'))
watch([textSignature, pxPerMm], () => {
  if (measureTimer) clearTimeout(measureTimer)
  measureTimer = setTimeout(measureTexts, 250)
})
function measureTexts() {
  if (store.editingTextId) { measureTimer = setTimeout(measureTexts, 400); return }
  const d = store.activeDoc
  if (!d) return
  for (const layer of d.layers) {
    if (layer.type !== 'text' || layer.autoFit !== 'shrink') continue
    const fitted = measureFittedSizePt(d, layer, pxPerMm.value)
    const next = Math.abs(fitted - layer.font.sizePt) < 0.01 ? undefined : fitted
    if ((layer.fittedSizePt ?? undefined) !== next) {
      // Messergebnis ist kein Undo-Schritt (abgeleiteter Wert)
      store.patchLayer(layer.id, { fittedSizePt: next } as any)
    }
  }
  store.overflowingTextLayerIds = measureOverflowingTextLayers(d, pxPerMm.value)
}

// ---------- Drag & Drop (Projektbilder aus dem Panel, Dateien vom Rechner) ----------
const dropHover = ref<{ layerId: string | null } | null>(null)
function dragKind(e: DragEvent): 'upload' | 'files' | null {
  const types = Array.from(e.dataTransfer?.types ?? [])
  if (getDraggedUpload() || types.includes(DESIGN_UPLOAD_MIME)) return 'upload'
  if (types.includes('Files')) return 'files'
  return null
}
/** Bild-Ebene unter dem Zeiger, die ein Foto aufnehmen darf (nicht gesperrt). */
function imageLayerAt(clientX: number, clientY: number): ImageLayer | null {
  for (const el of document.elementsFromPoint(clientX, clientY)) {
    const id = ((el as HTMLElement).closest?.('.dr-layer[data-layer-id]') as HTMLElement | null)?.dataset.layerId
    if (!id) continue
    const layer = props.doc.layers.find((l) => l.id === id)
    if (layer?.type === 'image' && !(layer.constraints.locked && !layer.constraints.contentOnly)) return layer
  }
  return null
}
function onDragOver(e: DragEvent) {
  if (!dragKind(e)) return
  e.preventDefault()
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  dropHover.value = { layerId: imageLayerAt(e.clientX, e.clientY)?.id ?? null }
}
function onDragLeave(e: DragEvent) {
  const to = e.relatedTarget as Node | null
  if (!to || !wrapEl.value?.contains(to)) dropHover.value = null
}
function onDrop(e: DragEvent) {
  const kind = dragKind(e)
  dropHover.value = null
  if (!kind) return
  e.preventDefault()
  const p = toMm(e.clientX, e.clientY)
  const target: StageDropTarget = { layerId: imageLayerAt(e.clientX, e.clientY)?.id ?? null, xMm: p.x, yMm: p.y }
  if (kind === 'upload') {
    let upload: UploadRef | null = null
    try { upload = JSON.parse(e.dataTransfer?.getData(DESIGN_UPLOAD_MIME) || 'null') } catch {}
    upload = upload?.uploadId ? upload : getDraggedUpload()
    if (upload?.uploadId && upload.url) emit('drop-upload', upload, target)
    return
  }
  const files = Array.from(e.dataTransfer?.files ?? [])
  if (files.length) emit('drop-files', files, target)
}
const dropHighlightStyle = computed(() => {
  const id = dropHover.value?.layerId
  const layer = id ? props.doc.layers.find((l) => l.id === id) : null
  if (!layer) return null
  const k = pxPerMm.value
  const b = props.doc.canvas.bleedMm
  return {
    left: `${(layer.frame.xMm + b) * k}px`,
    top: `${(layer.frame.yMm + b) * k}px`,
    width: `${layer.frame.wMm * k}px`,
    height: `${layer.frame.hMm * k}px`,
    transform: layer.frame.rotationDeg ? `rotate(${layer.frame.rotationDeg}deg)` : undefined,
  }
})

defineExpose({ zoom, pxPerMm })
</script>

<template>
  <div
    ref="wrapEl"
    class="stage"
    :class="{ 'stage--coarse': coarse }"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @wheel="onWheel"
    @dragenter="onDragOver"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <div class="stage__inner" :style="surfaceStyle">
      <div ref="surfaceEl" class="stage__surface">
        <DesignSurface :doc="doc" :px-per-mm="pxPerMm" :product-view="productView" :editing-text-id="store.editingTextId" @text-input="onTextInput" @text-done="onTextDone" />
        <GuidesOverlay v-if="geometry && guides !== 'off'" :geometry="geometry" :aspect="guideAspect" :mode="guides" :zones="doc.canvas.zones ?? null" />
        <svg v-if="snapLines.length" class="stage__snap" :viewBox="`0 0 ${fullW} ${fullH}`" preserveAspectRatio="none" aria-hidden="true">
          <line v-for="(l, i) in snapLines" :key="i"
            :x1="l.axis === 'x' ? l.mm + doc.canvas.bleedMm : 0" :x2="l.axis === 'x' ? l.mm + doc.canvas.bleedMm : fullW"
            :y1="l.axis === 'y' ? l.mm + doc.canvas.bleedMm : 0" :y2="l.axis === 'y' ? l.mm + doc.canvas.bleedMm : fullH"
            stroke="#2563eb" stroke-width="1" vector-effect="non-scaling-stroke" stroke-dasharray="4 3" />
        </svg>
        <div v-if="dropHover && dropHighlightStyle" class="stage__drop-slot" :style="dropHighlightStyle" aria-hidden="true" />
        <LayerHandles v-if="selectedLayer && !dropHover" :layer="selectedLayer" :canvas="doc.canvas" :px-per-mm="pxPerMm" :coarse="coarse" :in-slot="store.inSlotEditId === selectedLayer.id" @handle-down="onHandleDown" @rotate-down="onRotateDown" />
      </div>
    </div>
    <div v-if="dropHover && !dropHighlightStyle" class="stage__drop-hint" aria-hidden="true">Loslassen, um das Foto hinzuzufügen</div>
    <div class="stage__zoom" role="toolbar" aria-label="Ansicht">
      <button type="button" title="Verkleinern" aria-label="Verkleinern" :disabled="zoom <= 0.5" @click="zoom = Math.max(0.5, zoom / 1.25)"><Minus :size="15" /></button>
      <button type="button" class="stage__zoom-val" title="Einpassen (100 %)" aria-label="Auf Fensterbreite einpassen" @click="zoom = 1">{{ Math.round(zoom * 100) }} %</button>
      <button type="button" title="Vergrößern" aria-label="Vergrößern" :disabled="zoom >= 4" @click="zoom = Math.min(4, zoom * 1.25)"><Plus :size="15" /></button>
      <span class="stage__zoom-sep" aria-hidden="true" />
      <button type="button" :title="isFullscreen ? 'Vollbild beenden' : 'Vollbild'" :aria-label="isFullscreen ? 'Vollbild beenden' : 'Vollbild'" @click="toggleFullscreen"><Minimize2 v-if="isFullscreen" :size="15" /><Maximize2 v-else :size="15" /></button>
    </div>
  </div>
</template>

<style scoped>
.stage { position: relative; width: 100%; height: 100%; overflow: auto; background: #e9edf3; touch-action: none; user-select: none; -webkit-user-select: none; }
.stage__inner { position: relative; margin: 48px auto; box-shadow: 0 10px 30px rgba(15, 23, 42, 0.25); background: #fff; }
.stage--coarse .stage__inner { margin: 24px auto; }
.stage__surface { position: relative; width: 100%; height: 100%; }
.stage__snap { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; z-index: 4; }
.stage__drop-slot { position: absolute; z-index: 5; pointer-events: none; box-sizing: border-box; border: 3px solid var(--pfe-color-accent, #e30b17); background: rgba(227, 11, 23, 0.12); transform-origin: center; }
.stage__drop-hint { position: absolute; z-index: 6; left: 50%; top: 12px; transform: translateX(-50%); padding: 6px 12px; border-radius: 999px; background: rgba(15, 23, 42, 0.82); color: #fff; font-size: 12px; pointer-events: none; }
.stage__zoom { position: absolute; z-index: 6; right: 12px; bottom: 12px; display: inline-flex; align-items: center; gap: 4px; padding: 4px 6px; background: #fff; border: 1px solid rgba(15, 23, 42, 0.14); border-radius: 8px; font-size: 12px; }
.stage__zoom button { display: inline-flex; align-items: center; justify-content: center; min-width: 28px; height: 28px; padding: 0 4px; border: 0; border-radius: 6px; background: transparent; color: #0f172a; cursor: pointer; font: inherit; font-size: 12px; }
.stage__zoom button:hover:not(:disabled) { background: rgba(15, 23, 42, 0.06); }
.stage__zoom button:disabled { opacity: 0.35; cursor: default; }
.stage__zoom-val { min-width: 48px !important; font-variant-numeric: tabular-nums; }
.stage__zoom-sep { width: 1px; height: 18px; background: rgba(15, 23, 42, 0.12); margin: 0 2px; }
</style>
